"use client";

import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Citizen, Tag, Country, Gender } from "@/types";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { X, Check, ChevronsUpDown } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { TagsAPI, CountriesAPI, GendersAPI } from "@/lib/api";
import { cn, handleCitizenImageUpload } from "@/lib/utils";

// Add this type utility before the schema
type NestedKeyOf<ObjectType extends object> = {
  [Key in keyof ObjectType & (string | number)]: ObjectType[Key] extends object
    ? `${Key}.${NestedKeyOf<ObjectType[Key]>}`
    : `${Key}`;
}[keyof ObjectType & (string | number)];

// Define a more specific type for rating field paths
type RatingFieldPath =
  | "citizen_ratings.personality_score"
  | "citizen_ratings.likability_score"
  | "citizen_ratings.professionalism_score"
  | "citizen_ratings.influence_score"
  | "citizen_ratings.skill_score"
  | "citizen_ratings.social_responsibility_score"
  | "citizen_ratings.clout_score"
  | "citizen_ratings.political_spectrum_score";

const citizenFormSchema = z.object({
  citizen_name: z
    .string()
    .min(2, "Name must be at least 2 characters.")
    .max(100),
  citizen_bio_short: z
    .string()
    .max(200, "Short bio cannot exceed 200 characters")
    .optional(),
  citizen_bio: z
    .string()
    .max(600, "Full biography cannot exceed 600 characters")
    .optional(),
  is_verified: z.boolean().default(true),
  is_active: z.boolean().default(true),
  slug: z
    .string()
    .min(2, "Slug must be at least 2 characters.")
    .max(50, "Slug cannot exceed 50 characters")
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must contain only lowercase letters, numbers, and hyphens."
    ),
  gender_id: z.number().nullable().optional(),
  profile_pic: z
    .union([z.string().url().nullable(), z.literal(""), z.null()])
    .optional(),
  banner_pic: z
    .union([z.string().url().nullable(), z.literal(""), z.null()])
    .optional(),
  main_pic: z
    .union([z.string().url().nullable(), z.literal(""), z.null()])
    .optional(),
  twitter_url: z
    .string()
    .url()
    .max(250, "URL cannot exceed 250 characters")
    .optional()
    .or(z.literal("")),
  facebook_url: z
    .string()
    .url()
    .max(250, "URL cannot exceed 250 characters")
    .optional()
    .or(z.literal("")),
  instagram_url: z
    .string()
    .url()
    .max(250, "URL cannot exceed 250 characters")
    .optional()
    .or(z.literal("")),
  tiktok_url: z
    .string()
    .url()
    .max(250, "URL cannot exceed 250 characters")
    .optional()
    .or(z.literal("")),
  youtube_url: z
    .string()
    .url()
    .max(250, "URL cannot exceed 250 characters")
    .optional()
    .or(z.literal("")),
  website_url: z
    .string()
    .url()
    .max(250, "URL cannot exceed 250 characters")
    .optional()
    .or(z.literal("")),
  country_id: z
    .number({
      required_error: "Please select a country",
    })
    .min(1, "Please select a country"),
  tags: z
    .array(
      z.object({
        tag_id: z.number(),
        label: z.string(),
      })
    )
    .optional(),
  citizen_ratings: z.object({
    personality_score: z.number().min(0).max(10).default(0),
    likability_score: z.number().min(0).max(10).default(0),
    professionalism_score: z.number().min(0).max(10).default(0),
    influence_score: z.number().min(0).max(10).default(0),
    skill_score: z.number().min(0).max(10).default(0),
    social_responsibility_score: z.number().min(0).max(10).default(0),
    clout_score: z.number().min(0).max(10).default(0),
    political_spectrum_score: z.number().min(1).max(5).default(3),
    total_ratings: z.number().default(0),
  }),
});

type CitizenFormValues = z.infer<typeof citizenFormSchema>;

interface CitizenFormProps {
  initialData?: Citizen;
  onSubmit: (data: CitizenFormValues) => Promise<void>;
  isLoading?: boolean;
  isNewCitizen?: boolean;
}

interface ImageUploadProps {
  imageUrl: string;
  onUpload: (url: string) => void;
  type: "profile" | "main" | "banner";
  slug: string;
}

const ImageUpload = ({ imageUrl, onUpload, type, slug }: ImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const url = await handleCitizenImageUpload(file, type, slug);
      onUpload(url);
      toast.success(`${type} image uploaded successfully`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleClearImage = () => {
    onUpload("");
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Input
          type="url"
          placeholder={`Enter ${type} image URL`}
          value={imageUrl || ""}
          onChange={(e) => onUpload(e.target.value)}
          onBlur={(e) => {
            // If the field is empty on blur, ensure it's set to empty string
            if (!e.target.value.trim()) {
              onUpload("");
            }
          }}
        />
        <div className="flex gap-2">
          <div className="relative">
            <input
              type="file"
              id={`${type}-upload`}
              className="hidden"
              accept="image/jpeg,image/png,image/jpg"
              onChange={handleFileChange}
              disabled={isUploading}
            />
            <Button
              type="button"
              variant="outline"
              className="w-[100px]"
              disabled={isUploading}
              onClick={() => document.getElementById(`${type}-upload`)?.click()}
            >
              {isUploading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              ) : (
                "Upload"
              )}
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            className="w-[100px]"
            onClick={handleClearImage}
          >
            Clear
          </Button>
        </div>
      </div>
      {imageUrl && (
        <div
          className={cn(
            "relative overflow-hidden rounded-lg border",
            type === "profile"
              ? "aspect-square w-32"
              : type === "banner"
              ? "aspect-[21/9] w-full"
              : "aspect-video w-full"
          )}
        >
          <img
            src={imageUrl}
            alt={`${type} image`}
            className="h-full w-full object-cover"
          />
        </div>
      )}
    </div>
  );
};

// Update the rating fields array to use the specific type
const ratingFields: Array<{
  name: RatingFieldPath;
  label: string;
  max: number;
}> = [
  {
    name: "citizen_ratings.personality_score",
    label: "Personality",
    max: 10,
  },
  {
    name: "citizen_ratings.likability_score",
    label: "Likability",
    max: 10,
  },
  {
    name: "citizen_ratings.professionalism_score",
    label: "Professionalism",
    max: 10,
  },
  {
    name: "citizen_ratings.influence_score",
    label: "Influence",
    max: 10,
  },
  {
    name: "citizen_ratings.skill_score",
    label: "Skill",
    max: 10,
  },
  {
    name: "citizen_ratings.social_responsibility_score",
    label: "Social Responsibility",
    max: 10,
  },
  {
    name: "citizen_ratings.clout_score",
    label: "Clout",
    max: 10,
  },
  {
    name: "citizen_ratings.political_spectrum_score",
    label: "Political Spectrum",
    max: 5,
  },
];

// Add this helper component for character count display
interface CharacterCountProps {
  current: number;
  max: number;
  isError?: boolean;
}

const CharacterCount = ({ current, max, isError }: CharacterCountProps) => (
  <div
    className={cn(
      "text-sm text-muted-foreground text-right",
      isError && "text-destructive"
    )}
  >
    {current}/{max}
  </div>
);

export function CitizenForm({
  initialData,
  onSubmit,
  isLoading,
  isNewCitizen = false,
}: CitizenFormProps) {
  const router = useNavigate();
  const [searchResults, setSearchResults] = useState<Tag[]>([]);
  const [tagSearch, setTagSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [open, setOpen] = useState(false);
  const [genders, setGenders] = useState<Gender[]>([]);
  const [isLoadingGenders, setIsLoadingGenders] = useState(false);

  const handleFormSubmit = async (values: CitizenFormValues) => {
    try {
      // Convert empty strings to null for image fields
      const processedValues = {
        ...values,
        country_id: values.country_id || null,
        // Explicitly convert empty strings to null
        main_pic: values.main_pic === "" ? null : values.main_pic,
        profile_pic: values.profile_pic === "" ? null : values.profile_pic,
        banner_pic: values.banner_pic === "" ? null : values.banner_pic,
      };

      await onSubmit(processedValues);
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to save citizen");
    }
  };

  const defaultValues = useMemo(
    () => ({
      citizen_name: initialData?.citizen_name ?? "",
      citizen_bio_short: initialData?.citizen_bio_short ?? "",
      citizen_bio: initialData?.citizen_bio ?? "",
      is_verified: initialData?.is_verified ?? true,
      is_active: initialData?.is_active ?? true,
      slug: initialData?.slug ?? "",
      gender_id: initialData?.gender_id ?? null,
      profile_pic: initialData?.profile_pic ?? "",
      banner_pic: initialData?.banner_pic ?? "",
      main_pic: initialData?.main_pic ?? "",
      twitter_url: initialData?.twitter_url ?? "",
      facebook_url: initialData?.facebook_url ?? "",
      instagram_url: initialData?.instagram_url ?? "",
      tiktok_url: initialData?.tiktok_url ?? "",
      youtube_url: initialData?.youtube_url ?? "",
      website_url: initialData?.website_url ?? "",
      country_id: initialData?.country_id || 0,
      tags:
        initialData?.tags?.map((tag) => ({
          tag_id: tag.tag_id,
          label: tag.tag_label,
        })) ?? [],
      citizen_ratings: {
        personality_score: initialData?.citizen_ratings?.personality_score ?? 0,
        likability_score: initialData?.citizen_ratings?.likability_score ?? 0,
        professionalism_score:
          initialData?.citizen_ratings?.professionalism_score ?? 0,
        influence_score: initialData?.citizen_ratings?.influence_score ?? 0,
        skill_score: initialData?.citizen_ratings?.skill_score ?? 0,
        social_responsibility_score:
          initialData?.citizen_ratings?.social_responsibility_score ?? 0,
        clout_score: initialData?.citizen_ratings?.clout_score ?? 0,
        political_spectrum_score:
          initialData?.citizen_ratings?.political_spectrum_score ?? 3,
        total_ratings: initialData?.citizen_ratings?.total_ratings ?? 0,
      },
    }),
    [initialData]
  );

  const form = useForm<CitizenFormValues>({
    resolver: zodResolver(citizenFormSchema),
    defaultValues,
  });

  useEffect(() => {
    if (initialData) {
      // Create a new updates object with defaultValues and explicit image handling
      const formUpdates = {
        ...defaultValues,
        // Ensure image fields are properly handled
        main_pic: initialData.main_pic || "",
        profile_pic: initialData.profile_pic || "",
        banner_pic: initialData.banner_pic || "",
      };

      form.reset(formUpdates);
    }
  }, [initialData, form, defaultValues]);

  useEffect(() => {
    let isMounted = true;

    const fetchCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const data = await CountriesAPI.getAll();
        if (isMounted) {
          setCountries(data || []);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
        if (isMounted) {
          toast.error("Failed to load countries");
          setCountries([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingCountries(false);
        }
      }
    };

    fetchCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchGenders = async () => {
      setIsLoadingGenders(true);
      try {
        const data = await GendersAPI.getAll();
        if (isMounted) {
          setGenders(data || []);
        }
      } catch (error) {
        console.error("Error fetching genders:", error);
        if (isMounted) {
          toast.error("Failed to load genders");
          setGenders([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingGenders(false);
        }
      }
    };

    fetchGenders();

    return () => {
      isMounted = false;
    };
  }, []);

  const currentTags = form.watch("tags") ?? [];

  const handleTagSearch = async (value: string) => {
    setTagSearch(value);

    if (value.length >= 3) {
      setIsSearching(true);
      try {
        const results = await TagsAPI.search(value);
        setSearchResults(results ?? []);
      } catch (error) {
        console.error("Tag search error:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "citizen_name") {
        const newSlug = generateSlug(value.citizen_name || "");
        form.setValue("slug", newSlug, { shouldValidate: true });
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="space-y-8"
      >
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="space-y-8">
            <FormField
              control={form.control}
              name="citizen_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter citizen name" {...field} />
                  </FormControl>
                  <FormDescription>
                    The full name of the citizen.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gender_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gender</FormLabel>
                  <Select
                    value={field.value ? String(field.value) : ""}
                    onValueChange={(value) => {
                      field.onChange(value ? Number(value) : null);
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a gender">
                        {field.value
                          ? genders.find((gender) => gender.id === field.value)
                              ?.gender_name
                          : "Select a gender"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingGenders ? (
                        <div className="p-2 text-center">
                          <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                        </div>
                      ) : (
                        genders.map((gender) => (
                          <SelectItem key={gender.id} value={String(gender.id)}>
                            {gender.gender_name}
                            {gender.pronouns && ` (${gender.pronouns})`}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Select the gender identity of the citizen.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="country_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country</FormLabel>
                  <Select
                    value={field.value ? String(field.value) : ""}
                    onValueChange={(value) => {
                      field.onChange(Number(value));
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select a country">
                        {field.value
                          ? countries.find(
                              (country) => country.country_id === field.value
                            )?.country_name
                          : "Select a country"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingCountries ? (
                        <div className="p-2 text-center">
                          <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                        </div>
                      ) : (
                        countries.map((country) => (
                          <SelectItem
                            key={country.country_id}
                            value={String(country.country_id)}
                          >
                            {country.country_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormDescription>
                    Select the country this citizen is primarily associated
                    with.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input placeholder="citizen-slug" {...field} />
                  </FormControl>
                  <FormDescription>
                    URL-friendly version of the name (used in URLs).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="citizen_bio_short"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Short Bio</FormLabel>
                  <FormControl>
                    <div className="space-y-1">
                      <Textarea
                        placeholder="Brief description (max 200 characters)"
                        className={cn(
                          "resize-none",
                          form.formState.errors.citizen_bio_short &&
                            "border-destructive"
                        )}
                        maxLength={200}
                        {...field}
                      />
                      <CharacterCount
                        current={field.value?.length || 0}
                        max={200}
                        isError={!!form.formState.errors.citizen_bio_short}
                      />
                    </div>
                  </FormControl>
                  <FormDescription>
                    A short bio that appears in citizen cards and previews.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-8">
            <FormField
              control={form.control}
              name="is_verified"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Verified Status</FormLabel>
                    <FormDescription>
                      Mark this citizen as officially verified.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active Status</FormLabel>
                    <FormDescription>
                      Is this citizen profile visible to users?
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <FormLabel>Tags</FormLabel>
              <div className="flex flex-wrap gap-2 mb-2">
                {currentTags.map((tag) => (
                  <Badge
                    key={tag.tag_id}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {tag.label}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground"
                      onClick={() => {
                        const newTags = currentTags.filter(
                          (t) => t.tag_id !== tag.tag_id
                        );
                        form.setValue("tags", newTags);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </Badge>
                ))}
              </div>
              <div className="relative">
                <Input
                  placeholder="Search tags (type at least 3 characters)"
                  value={tagSearch}
                  onChange={(e) => handleTagSearch(e.target.value)}
                  className="w-full"
                />

                {(searchResults.length > 0 ||
                  (isSearching && tagSearch.length >= 3)) && (
                  <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md">
                    {isSearching ? (
                      <div className="p-2 text-center">
                        <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                      </div>
                    ) : (
                      <div className="py-1">
                        {searchResults.map((tag) => {
                          const isSelected = currentTags.some(
                            (t) => t.tag_id === tag.tag_id
                          );
                          return (
                            <button
                              key={tag.tag_id}
                              className={cn(
                                "w-full px-3 py-2 text-left hover:bg-accent",
                                isSelected && "opacity-50 cursor-not-allowed"
                              )}
                              onClick={() => {
                                if (!isSelected) {
                                  const newTags = [...currentTags, tag];

                                  form.setValue("tags", newTags);
                                  setTagSearch("");
                                  setSearchResults([]);
                                }
                              }}
                              disabled={isSelected}
                            >
                              <span className="flex items-center">
                                {tag.label}
                                {isSelected && (
                                  <Check className="ml-2 h-4 w-4" />
                                )}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <FormDescription>
                Search and select tags to categorize the citizen.
              </FormDescription>
            </div>
          </div>
        </div>

        {isNewCitizen && (
          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-medium mb-4">Ratings</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {ratingFields.map(({ name, label, max }) => (
                  <FormField
                    key={name}
                    control={form.control}
                    name={name}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{label}</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-4">
                            <Input
                              type="number"
                              min={0}
                              max={max}
                              step={0.1}
                              value={field.value || ""}
                              onChange={(e) => {
                                const value =
                                  e.target.value === ""
                                    ? 0
                                    : Number(e.target.value);
                                field.onChange(value);
                              }}
                              onBlur={field.onBlur}
                              name={field.name}
                            />
                            <span className="text-sm text-muted-foreground">
                              / {max}
                            </span>
                          </div>
                        </FormControl>
                        <FormDescription>
                          Rate from 0 to {max}
                          {name ===
                            "citizen_ratings.political_spectrum_score" &&
                            " (3 is center)"}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="citizen_bio"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Biography</FormLabel>
              <FormControl>
                <div className="space-y-1">
                  <Textarea
                    placeholder="Detailed biography"
                    className={cn(
                      "min-h-32",
                      form.formState.errors.citizen_bio && "border-destructive"
                    )}
                    maxLength={600}
                    {...field}
                  />
                  <CharacterCount
                    current={field.value?.length || 0}
                    max={600}
                    isError={!!form.formState.errors.citizen_bio}
                  />
                </div>
              </FormControl>
              <FormDescription>
                A comprehensive biography of the citizen.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="space-y-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <FormField
              control={form.control}
              name="profile_pic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Profile Picture</FormLabel>
                  <FormControl>
                    <ImageUpload
                      imageUrl={field.value}
                      onUpload={field.onChange}
                      type="profile"
                      slug={form.getValues("slug")}
                    />
                  </FormControl>
                  <FormDescription>
                    Square image for profile (max 2MB).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="main_pic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Main Picture</FormLabel>
                  <FormControl>
                    <ImageUpload
                      imageUrl={field.value}
                      onUpload={field.onChange}
                      type="main"
                      slug={form.getValues("slug")}
                    />
                  </FormControl>
                  <FormDescription>
                    Main featured image (max 3MB).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="banner_pic"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Banner Picture</FormLabel>
                  <FormControl>
                    <ImageUpload
                      imageUrl={field.value}
                      onUpload={field.onChange}
                      type="banner"
                      slug={form.getValues("slug")}
                    />
                  </FormControl>
                  <FormDescription>
                    Wide banner image (max 3MB).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="space-y-8">
            <FormField
              control={form.control}
              name="twitter_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Twitter URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://twitter.com/handle"
                      maxLength={250}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="facebook_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facebook URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://facebook.com/page"
                      maxLength={250}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="instagram_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instagram URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://instagram.com/handle"
                      maxLength={250}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-8">
            <FormField
              control={form.control}
              name="tiktok_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>TikTok URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://tiktok.com/@handle"
                      maxLength={250}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="youtube_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>YouTube URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://youtube.com/channel"
                      maxLength={250}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="website_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Website URL</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://example.com"
                      maxLength={250}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Button type="submit" disabled={isLoading}>
            {isLoading
              ? "Saving..."
              : initialData
              ? "Update Citizen"
              : "Create Citizen"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => router("/citizens")}
          >
            Cancel
          </Button>
        </div>
      </form>
    </Form>
  );
}
